import { Component, OnDestroy, OnInit } from '@angular/core';
import { Nanium } from 'nanium/core';
import { NaniumConsumerBrowserHttp } from 'nanium/managers/consumers/browserHttp';
import { WebRequestInterceptorService } from '../services/security/web-request-interceptor.service';
import { WebAuthenticationService } from '../services/security/web-authentication.service';
import { Router } from '@angular/router';
import { User } from '../../../server/entities/user.entity';
import {environment} from "../environments/environment";
import { FirebaseMessagingService } from '../services/firebase-messaging/firebase-messaging.service';
import { Platform } from '@ionic/angular';
import { SharedDataService } from '../services/shared-data/shared-data.service';
import { PageViewTrackingService } from '../services/analytics/page-view-tracking.service';
import { App as CapacitorApp, AppState } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public user: User;

  /**
   * Capacitor App-resume listener. Triggers a fast location refresh
   * whenever the app comes back to the foreground so users who travel
   * to another city for a day get up-to-date "nearby" results on
   * return without having to manually pull-to-refresh anywhere.
   *
   * `null` when the listener hasn't been registered (e.g. on web
   * platforms where `@capacitor/app` is a no-op until subscribed).
   */
  private appStateListenerHandle: PluginListenerHandle | null = null;

  /**
   * Handle for the FCM notification-tap listener. `null` until registered
   * (and on web, where the plugin is a no-op).
   */
  private notificationTapListenerHandle: PluginListenerHandle | null = null;

  /**
   * Deep-link captured from a notification tap that arrived before the app
   * had finished bootstrapping (i.e. a tap that cold-started the app).
   * Navigated to once `appReadyForDeepLink` flips to `true`; `null` when
   * there is nothing pending.
   */
  private pendingDeepLink: string | null = null;

  /** `true` once `ngOnInit` has finished init and router navigation is safe. */
  private appReadyForDeepLink = false;

  constructor(
    private webRequestInterceptorService: WebRequestInterceptorService,
    private webAuthenticationService: WebAuthenticationService,
    private router: Router,
    private firebaseMessagingService: FirebaseMessagingService,
    private platform: Platform,
    private sharedDataService: SharedDataService,
    private pageViewTrackingService: PageViewTrackingService
  ) {}

  async ngOnInit() {

    // Register the notification-tap listener as early as possible so a tap
    // that cold-started the app (launch notification) is not missed. Actual
    // navigation is deferred until init finishes (see appReadyForDeepLink).
    await this.registerNotificationTapListener();

    let serverBaseUrl: string = '';
    // serverBaseUrl = environment.apiEndpoint;
    if (environment.production) {
      serverBaseUrl = environment.apiEndpoint;
    } else if (location.port === '4200' || location.port === '4201' || location.port === '8100' || location.port === '8101') {
      serverBaseUrl = 'http://' + location.host.replace(/:[0-9]{4}/, ':3001');
    }

    await Nanium.addManager(new NaniumConsumerBrowserHttp({
      apiUrl: serverBaseUrl + '/api',
      apiEventUrl: serverBaseUrl + '/events',
      onServerConnectionRestored: () => {
        console.log('server connection restored');
        // when the clients could not reach the server for some time,
        // you should reload all stuff you need because maybe you missed some events
      },
      eventSubscriptionSendInterceptors: [],
      requestInterceptors: [ this.webRequestInterceptorService ],
      handleError: async (error: any) => {
        console.log(error, this.router.url);
        if (
          error === 'unauthorized'
          || error instanceof Error && error.message == 'unauthorized'
        ) {
          if (this.router.url === '/login') {
            return;
          }
          await this.router.navigate(['/onboarding']);
        }
      }
    }));

    this.pageViewTrackingService.init();

    await this.webAuthenticationService.init();
    this.user = await this.webAuthenticationService.getUser();

    if (this.user?.id !== undefined) {
      await this.platform.ready();
      await this.sharedDataService.updateUser(this.user);
      await this.firebaseMessagingService.updateTokenOfUser();
    }

    await this.platform.ready();
    this.kickOffStartupLocationFetch();
    this.registerAppResumeLocationRefresh();
    this.clearPushBadge();

    // Init is complete — it is now safe to route to any deep link that a
    // launch-notification tap captured while we were still bootstrapping.
    this.appReadyForDeepLink = true;
    this.flushPendingDeepLink();
  }

  ngOnDestroy(): void {
    this.appStateListenerHandle?.remove();
    this.appStateListenerHandle = null;
    this.notificationTapListenerHandle?.remove();
    this.notificationTapListenerHandle = null;
  }

  /**
   * Fire-and-forget the fast-phase device fix on app start. Used to
   * live in `home.loadData()`, but doing it here means the central
   * store is being populated regardless of which page the user lands
   * on (deep links, login flows, etc.) and any later
   * `updateUserLocation()` from `home.loadData` is deduplicated by
   * the in-flight promise tracking inside `SharedDataService`.
   */
  private kickOffStartupLocationFetch(): void {
    this.sharedDataService.updateUserLocation().catch(err => {
      console.log('Startup location fetch failed (non-fatal):', err);
    });
  }

  /**
   * On Capacitor's `appStateChange` -> `isActive: true` (i.e. resume
   * from background), refresh the location. Cheap because the fast
   * phase reuses the OS's cached fix when fresh and otherwise resolves
   * in <1s on Wi-Fi. Avoids the need for any continuous GPS watch.
   */
  private async registerAppResumeLocationRefresh(): Promise<void> {
    try {
      this.appStateListenerHandle = await CapacitorApp.addListener('appStateChange', (state: AppState) => {
        if (!state.isActive) return;
        this.clearPushBadge();
        this.sharedDataService.updateUserLocation().catch(err => {
          console.log('App-resume location refresh failed (non-fatal):', err);
        });
      });
    } catch (err) {
      // Capacitor App plugin may be unavailable on certain web targets;
      // missing it just means we lose the resume refresh, not the
      // startup refresh, so this is non-fatal.
      console.log('Could not register Capacitor App listener:', err);
    }
  }

  /**
   * Clear delivered push notifications on startup and resume. NOTE: this
   * does NOT zero the iOS app-icon badge — `@capacitor-firebase/messaging`
   * has no badge API, and iOS keeps badges sticky until explicitly reset.
   * The badge reset still needs a native call (AppDelegate) or a dedicated
   * badge plugin; tracked separately.
   */
  private async clearPushBadge(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await FirebaseMessaging.removeAllDeliveredNotifications();
    } catch (err) {
      console.log('Could not clear delivered notifications (non-fatal):', err);
    }
  }

  /**
   * Subscribe to notification taps. The server attaches a `deepLinkUrl`
   * (e.g. `/news/<id>`, `/facility/<id>`, `/gathering/<id>`) to every
   * push's data payload (see the server `triggerService`). Without this
   * listener the payload arrives on the device but is never read, so a tap
   * just opens the app on the home screen instead of the announced content.
   */
  private async registerNotificationTapListener(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      this.notificationTapListenerHandle = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event) => {
          const data = event?.notification?.data as { deepLinkUrl?: string } | undefined;
          const url = data?.deepLinkUrl;
          if (!url) {
            console.warn('[AppComponent] Notification tap without deepLinkUrl — ignoring');
            return;
          }
          this.navigateToDeepLink(url);
        }
      );
    } catch (err) {
      // Non-fatal: the plugin may be unavailable on some targets. We just
      // lose deep-link navigation, not push delivery.
      console.log('Could not register notification-tap listener:', err);
    }
  }

  /**
   * Route to a notification deep link, or buffer it until init has finished
   * when the tap arrived during a cold start.
   */
  private navigateToDeepLink(url: string): void {
    if (!this.appReadyForDeepLink) {
      this.pendingDeepLink = url;
      return;
    }
    this.router.navigateByUrl(url).catch(err => {
      console.log('Deep-link navigation failed:', url, err);
    });
  }

  /** Navigate to any deep link captured before the app was ready. */
  private flushPendingDeepLink(): void {
    if (this.pendingDeepLink) {
      const url = this.pendingDeepLink;
      this.pendingDeepLink = null;
      this.navigateToDeepLink(url);
    }
  }

  hasUser(): boolean {
    return this.user !== undefined;
  }

  shouldShowFooter(): boolean {
    const currentRoute = this.router.url;
    return !(currentRoute.includes('/onboarding'));
  }
}
